/*
 * Component enabling customers to view subscriptions
 * - Customers can update email and password.  
 */
import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import { Redirect } from 'react-router-dom';
import AbEnvironment from '../auth/AbEnvironment';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import { Button, Grid, IconButton, Paper, TextField, Tooltip, Typography } from '@mui/material';

import Cancel from '@mui/icons-material/Cancel';
import CheckCircle from '@mui/icons-material/CheckCircle';
import CallMade from '@mui/icons-material/CallMade';

import { getValueForRender } from '../../utils/InputUtils';
import AppDisplay from '../ui/AppDisplay';
import PageTitle from '../ui/PageTitle';
import LoadingSpinner from '../ui/LoadingSpinner';
import SaveButton from '../ui/SaveButton';
import ServerError from '../ui/ServerError';
import PasswordInput from '../ui/PasswordInput';
import AppBindStyles from '../../AppBindStyles';
import CustomerAppAudit from '../app/CustomerAppAudit';
import TaskRibbon from '../tasks/TaskRibbon'
import UpdateFlowStepMutation from '../tasks/UpdateFlowStepMutation'

import { red, green } from '@mui/material/colors';

const styleSheet = theme => ({
    root: {
      width: '100%',
    },
    emailField: {
      minWidth: 336,
    },
    additionalFields: {
      minWidth: 336,
    },
    iconButton: {
      paddingLeft: 8,
      paddingRight: 8,
      paddingTop: 2,
      paddingBottom: 2,
    },
    iconButtonAction: {
      paddingLeft: 8,
      paddingRight: 8,
      paddingTop: 3,
      paddingBottom: 2,
    },
    iconButtonRightMargin: {
      paddingLeft: 8,
      paddingRight: 8,
      paddingTop: 2,
      paddingBottom: 2,
      marginRight: 36,
    },
    paper: {
      textAlign: 'left',
      maxWidth: 1200,
    },
    [theme.breakpoints.up('xs')]: {
      paper: {
        padding: theme.spacing(1)
      }
    },
    [theme.breakpoints.up('sm')]: {
      paper: {
        padding: theme.spacing(2)
      }
    },
    [theme.breakpoints.up('md')]: {
      paper: {
        padding: theme.spacing(3)
      }
    }
});

const abEnvironment = new AbEnvironment();
// The query is redundant - this is a hack in order to pass a task ribbon component what it expects
const SubscriptionViewQuery = graphql`
query SubscriptionViewQuery($customerAppId: UUID!) {
  customerAppTByCustomerAppId(customerAppId: $customerAppId) {
    customerAppId
    appId
    customerAccountId
    provisioningAccountId
    appStatus
    notes
    bcId
    sharedEmail
    useEmail
    domain
    username
    pwd
    shareCredentials
    showSubscription
    appTByAppId {
      appName
      appSku
      appUrl
      appLogo
      appType
      description
      signupUrl
      showDomain
      showUsername
    }
    bindCardTByBcId {
      extBcId
      bcStatus
      usingAccountId
      provisioningAccountId
      firstName
      lastName
      address1
      city
      state
      postalCode
    }
    accountTByCustomerAccountId {
      accountName
    }
    flowTaskTsByCustomerAppId {
      nodes {
        flowTaskId
        taskType
        taskData
        title
        instruction
        linkName
        linkUrl
        customerAppId
        customerAppTByCustomerAppId {
          customerAppId
          customerAccountId
          provisioningAccountId
          accountTByProvisioningAccountId {
            accountName
          }
          appId
          appStatus
          sharedEmail
          useEmail
          domain
          username
          pwd
          appTByAppId {
            appName
            appLogo
            showDomain
            showUsername
          }
        }
        flowStepTsByFlowTaskId {
          nodes {
            flowStepId
            stepState
          }
        }
      }
    }
  }
}
`;

class SubscriptionView extends Component {

    constructor(props) {
      super(props);

      this.state = {
        unencryptPwd:         null,
        unencryptPwdChanged:  false,
        domain:               null,
        username:             null,
        pwd:                  null,
        useEmail:             null,
        forceFetch:           0,
        saveStatus:           null,
        saveMessage:          null,
        saveSnackbarOpen:     false,
        saveSnackbarDuration: null
      };
    }

    componentDidMount() {
      if (abEnvironment.isSegmentActive()) { window.analytics.page(); }
    }
  
    goBack() {
      this.props.history.goBack();
    }
  
    setClipboardVal(theText) {
      navigator.clipboard.writeText(theText)
      .then(() => {
        //
      })
      .catch(err => {
        //
      })
    }

    setUnencryptedPassword(value) {
      this.setState({unencryptPwdChanged: true, unencryptPwd: value});
    }

    isShowDomain(theSubscription) {
      // Is there a task setting for the subscription?
      let theTask = null;
      if (theSubscription.flowTaskTsByCustomerAppId.nodes.length > 0) {
        theTask = theSubscription.flowTaskTsByCustomerAppId.nodes[0];
        if (theTask.taskType === 0) {
          let taskData = theTask.taskData;
          if (taskData.domain === true) {
            return true;
          }
        }
      }

      // Is there an app domain setting?
      let theApp = theSubscription.appTByAppId;
      if (theApp.showDomain === 1) {
        return true;
      }

      return false;
    }

    isShowUsername(theSubscription) {
      // Is there a task setting for the subscription?
      let theTask = null;
      if (theSubscription.flowTaskTsByCustomerAppId.nodes.length > 0) {
        theTask = theSubscription.flowTaskTsByCustomerAppId.nodes[0];
        if (theTask.taskType === 0) {
          let taskData = theTask.taskData;
          if (taskData.username === true) {
            return true;
          }
        }
      }

      // Is there an app username setting?
      let theApp = theSubscription.appTByAppId;
      if (theApp.showUsername === 1) {
        return true;
      }
      
      return false;
    }

    disableSave(theApp, theSubscription) {
      const { validateEmail } = this.props.auth;

      if (theSubscription === null)
        return true;
  
      if (this.state.useEmail) {
        let emailOk = validateEmail(this.state.useEmail);
        if (! emailOk)
          return true;
      }           

      if (this.state.useEmail === '') {
        //user is trying to blank out useEmail - do not allow;
        return true;
      }

      let somethingChanged = false;
      if (this.isShowDomain(theSubscription)) {
        if (this.state.domain != null && this.state.domain !== '' && this.state.domain !== theSubscription.domain) {
          somethingChanged = true;
        }  
      }

      if (this.isShowUsername(theSubscription)) {
        if (this.state.username != null && this.state.username !== '' && this.state.username !== theSubscription.username) {
          somethingChanged = true;
        }
      }

      if (this.state.useEmail != null && this.state.useEmail !== '' && this.state.useEmail !== theSubscription.useEmail) {
        somethingChanged = true;
      }

      if (this.state.unencryptPwdChanged) {
        somethingChanged = true;
      }

      if (!somethingChanged) {
        return true;
      }

      return false;
    }

    markStepDone = (stepId) => {
      Promise.resolve()  
      .then(() => {  
        return UpdateFlowStepMutation.commit(stepId, 1);
      }).then((response) => {
        this.setState({forceFetch: this.state.forceFetch + 1});
      })
      .catch(error => {
        // console.log('Got error: ' + error);
      });
    }
  
    markStepPending = (stepId) => {
      Promise.resolve()  
      .then(() => {  
        return UpdateFlowStepMutation.commit(stepId, 0);
      }).then((response) => {
        this.setState({forceFetch: this.state.forceFetch + 1});
      })
      .catch(error => {
        // console.log('Got error: ' + error);
      });
    }
  
    render() {
      const { isAuthenticated, isUnauthenticatedCustomer, isAuthenticatedCustomer, needAssent, logError } = this.props.auth;
      const classes = this.props.classes;
      const cancelColor   = red[300];
      const okColor = green[300];
  
      if (!isAuthenticated() || needAssent()) {
        return (
          <Redirect to='/home' />
        );
      } else if (isAuthenticatedCustomer() || isUnauthenticatedCustomer()) {
        const params = new URLSearchParams(this.props.location.search);
        let customerAppId = params.get('customerAppId');
  
        let fp = params.get('fp');
        let backTo = "Subscriptions";
        if (fp != null && fp !== '') {
          backTo = fp;
        }

        return (
          <QueryRenderer
            environment={abEnvironment.getEnvironment()}
            query={SubscriptionViewQuery}
            variables={{customerAppId: customerAppId, forceFetch: this.state.forceFetch}}
            render={({error, props})=> {
              if (error) {
                logError(error.message);
                return (
                  <ServerError/>
                );
              } else if (props) {
                if (props.customerAppTByCustomerAppId === null)
                {
                  return (
                    <div className={classes.root}>
                      <PageTitle title="Subscription not found" showBackButton={true} name={backTo} history={this.props.history}/>
                    </div>
                  );
                }
  
                let theSubscription = props.customerAppTByCustomerAppId;
                let theApp = theSubscription.appTByAppId;
                let theBindcard = theSubscription.bindCardTByBcId;
  
                let isAppCancelled = false;
                if (theSubscription.appStatus === 2) {
                  isAppCancelled = true;
                }
                //this is technically not true - a 1 means inactive but for now we are doing this as extra protection.
                if (theSubscription.appStatus === 1) {
                  isAppCancelled = true;
                }
    
                let authOnlySubscription = false;
                if (theBindcard.bcStatus === 4) {
                  authOnlySubscription = true;
                }

                //first make sure link url is prefixed correctly
                let loginDisabled = true;
                let appUrlWithProtocol = '';
                if (theApp.appUrl && theApp.appUrl !== '') {
                  appUrlWithProtocol = (theApp.appUrl.startsWith("http://") || theApp.appUrl.startsWith("https://")) ? theApp.appUrl :  "https://" + theApp.appUrl;
                  loginDisabled = false;
                }
                 
                let theUsePwdVal   = getValueForRender(this.state.pwd, theSubscription.pwd, '');
                let theUseEmailVal = getValueForRender(this.state.useEmail, theSubscription.useEmail, theSubscription.sharedEmail, null);
                let theDomainVal   = getValueForRender(this.state.domain, theSubscription.domain, '');
                let theUsernameVal = getValueForRender(this.state.username, theSubscription.username, '');

                // let savedEmail = getValueForRender(theSubscription.useEmail, theSubscription.sharedEmail, null);

                /* Allow nav to app even if fields aren't set
                if (theSubscription.pwd == null || theSubscription.pwd === '' || savedEmail == null || savedEmail === '' || isAppCancelled === true) {
                  loginDisabled = true;
                }
                if (theApp.showDomain === 1 && (theSubscription.domain == null || theSubscription.domain === '')) {
                  loginDisabled = true;
                }
                if (theApp.showUsername === 1 && (theSubscription.username == null || theSubscription.username === '')) {
                  loginDisabled = true;
                }
                */
                let pTitle = 'Subscription to ' + theApp.appName  + ' for ' + theSubscription.accountTByCustomerAccountId.accountName;

                let subscriptionModel = 0; // Share credentials
                let taskData = null;
                let theTask = null;
                let theStepId = null;
                let theStepState = null;
                let theStepDone = false;
                if (theSubscription.flowTaskTsByCustomerAppId.nodes.length > 0) {
                  theTask = theSubscription.flowTaskTsByCustomerAppId.nodes[0];
                  subscriptionModel = theTask.taskType;
                  taskData = theTask.taskData;
                  let theSteps = theTask.flowStepTsByFlowTaskId.nodes;
                  if (theSteps.length > 0) {
                    theStepId = theSteps[0].flowStepId;
                    theStepState = theSteps[0].stepState;
                    if (theStepState === 1) {
                      theStepDone = true;
                    } 
                  }
                }

                return (
                  <Grid container spacing={3} direction="row" justifyContent="flex-start">
                    <Grid item xs={12}>
                      <PageTitle title={pTitle} showBackButton={true} name={backTo} noMargin={true} history={this.props.history}/>
                    </Grid>
                    <Grid item xs={12}>
                      <Grid container spacing={2} direction="row" >
                        <Grid item xs={12}>
                          <Paper className={classes.paper}>
                            <Grid container spacing={0} direction="row" justifyContent='space-between'>
                              <Grid item xs={10}>
                                <AppDisplay appLogo={theApp.appLogo}
                                            appName={theApp.appName}
                                            appSku={theApp.appSku}
                                            description={theApp.description}/>
                              </Grid>

                              { theBindcard.extBcId !== null && !isAppCancelled &&
                              <Grid item>
                                <CheckCircle htmlColor={okColor} />
                              </Grid>
                              }
                              { theBindcard.extBcId !== null && isAppCancelled &&
                              <Grid item>
                                <Cancel htmlColor={cancelColor} />
                              </Grid>
                              }
                              { theBindcard.extBcId === null && !isAppCancelled && authOnlySubscription &&
                              <Grid item>
                                <CheckCircle htmlColor={okColor} />
                              </Grid>
                              }
                              { theBindcard.extBcId === null && isAppCancelled && authOnlySubscription &&
                              <Grid item>
                                <Cancel htmlColor={cancelColor} />
                              </Grid>
                              }
                            </Grid>
                          </Paper>
                        </Grid>

                        <Grid item xs={12} >
                          {subscriptionModel === 0 && theStepState !== 0 &&
                          <Paper className={classes.paper}>
                            <Grid container spacing={2} justifyContent="space-between" alignItems="center">
                              <Grid item>
                                <Grid container spacing={0}>
                                  <Grid item xs={12}>
                                    <Typography align="left" variant="h6">How to log in</Typography>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Typography variant="body2">Credentials used to login</Typography>
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item>
                                <Button color="primary" 
                                        variant="outlined" 
                                        disabled={loginDisabled} 
                                        onClick={() => { this.goToExternalApp('appLogin', theSubscription.customerAppId, null, appUrlWithProtocol) }}
                                >
                                  Log in&nbsp;<CallMade/>
                                </Button>
                              </Grid>

                              {/* Domain */}
                              {this.isShowDomain(theSubscription) === true &&
                              <Grid item xs={12}>
                                <Grid container spacing={1} justifyContent="space-between" alignItems="center">
                                  <Grid item>
                                    <Grid container direction="column" spacing={0}>
                                      <Grid item>
                                        <Typography variant="body1" style={AppBindStyles.boldLabel}>Domain</Typography>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                  <Grid item>
                                    <Grid container justifyContent="flex-start" alignItems="center" spacing={1}>
                                      <Grid item>
                                        <TextField
                                          id="domain" className={classes.additionalFields}
                                          value={theDomainVal}
                                          onChange={ (e) => { this.setState({ domain: e.target.value }); } }
                                        />
                                      </Grid>
                                      <Grid item>
                                        <Tooltip placement='bottom' title='Copy domain to clipboard'>
                                          <IconButton
                                            className={classes.iconButtonRightMargin}
                                            onClick={() => {this.setClipboardVal(theDomainVal); }}
                                            size="large">
                                            <img alt="Copy to Clipboard" 
                                                src='/images/content_copy_black_24x24.png' 
                                                height="16px"/>
                                          </IconButton>
                                        </Tooltip>     
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Grid>}

                              {/* Username */}
                              {this.isShowUsername(theSubscription) === true &&
                              <Grid item xs={12}>
                                <Grid container spacing={1} justifyContent="space-between" alignItems="center">
                                  <Grid item>
                                    <Grid container direction="column" spacing={0}>
                                      <Grid item>
                                        <Typography variant="body1" style={AppBindStyles.boldLabel}>User name</Typography>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                  <Grid item>
                                    <Grid container justifyContent="flex-start" alignItems="center" spacing={1}>
                                      <Grid item>
                                        <TextField
                                          id="username" 
                                          className={classes.additionalFields}
                                          disabled={isAppCancelled}
                                          value={theUsernameVal}
                                          onChange={ (e) => { this.setState({ username: e.target.value }); } }
                                        />
                                      </Grid>
                                      <Grid item>
                                        <Tooltip placement='bottom' title='Copy username to clipboard'>
                                          <IconButton
                                            className={classes.iconButtonRightMargin}
                                            onClick={() => {this.setClipboardVal(theUsernameVal); }}
                                            size="large">
                                            <img alt="Copy to Clipboard" 
                                                src='/images/content_copy_black_24x24.png' 
                                                height="16px"/>
                                          </IconButton>
                                        </Tooltip>     
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Grid>}

                              {/* Email */}
                              <Grid item xs={12}>
                                <Grid container spacing={1} justifyContent="space-between" alignItems="center">
                                  <Grid item>
                                    <Typography variant="body1" style={AppBindStyles.boldLabel}>Email</Typography>
                                  </Grid>
                                  <Grid item>
                                    <Grid container justifyContent="flex-start" alignItems="center" spacing={1}>
                                      <Grid item>
                                        <TextField className={classes.emailField} id="your-provided-email" 
                                          disabled={isAppCancelled}
                                          value={theUseEmailVal}
                                          onChange={(e) => { this.setState({ useEmail: e.target.value });}}
                                        />
                                      </Grid>
                                      <Grid item>
                                        <Tooltip placement='bottom' title='Copy email to clipboard'>
                                          <IconButton
                                            className={classes.iconButtonRightMargin}
                                            onClick={() => {this.setClipboardVal(theUseEmailVal); }}
                                            size="large">
                                            <img alt="Copy to Clipboard" 
                                                src='/images/content_copy_black_24x24.png' 
                                                height="16px"/>
                                          </IconButton>
                                        </Tooltip>     
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Grid>

                              {/* Password */}
                              <Grid item xs={12}>
                                <Grid container spacing={1} justifyContent="space-between" alignItems="center">
                                  <Grid item>
                                    <Typography variant="body1" style={AppBindStyles.boldLabel}>Password</Typography>
                                  </Grid>
                                  <Grid item>
                                    <PasswordInput
                                      name="password"
                                      disabled={isAppCancelled}
                                      auth={this.props.auth}
                                      customerAppId={theSubscription.customerAppId}
                                      pwd={theUsePwdVal}
                                      onChange={(e) => {this.setUnencryptedPassword(e)}}
                                    />
                                  </Grid>
                                </Grid>
                              </Grid>  
    
                              <Grid item xs={12} >
                                <SaveButton
                                  buttonStyle={AppBindStyles.bottomButtonPaper6}
                                  snackbarOpen={this.state.saveSnackbarOpen}
                                  autoHideDuration={this.state.saveSnackbarDuration}
                                  status={this.state.saveStatus} 
                                  message={this.state.saveMessage}
                                  disabled={this.disableSave(theApp, theSubscription)}
                                  onSaveClick={() => { this._updateCustomerApp(theApp, theSubscription); }}
                                  onSnackbarClose={() => { this._onSnackbarClose(); }}
                                />
                              </Grid>
                            </Grid>
                          </Paper>}
                          {subscriptionModel === 0 && theStepState === 0 &&
                          <Paper className={classes.paper}>
                            <TaskRibbon
                              containerName='SubscriptionView'
                              auth={this.props.auth}
                              stepId={theStepId}
                              task={theTask}
                              done={theStepDone}
                              completeTask={this.markStepDone}
                              undoTask={this.markStepPending}
                            />
                          </Paper>}                               
                          {subscriptionModel === 1 &&
                          <Paper className={classes.paper}>
                                <TaskRibbon
                                  containerName='SubscriptionView'
                                  auth={this.props.auth}
                                  stepId={theStepId}
                                  task={theTask}
                                  done={theStepDone}
                                  completeTask={this.markStepDone}
                                  undoTask={this.markStepPending}
                                />
                           </Paper>}                        
                        </Grid>
                        <Grid item xs={12}>
                          <Paper className={classes.paper}>
                            <CustomerAppAudit auth={this.props.auth} history={this.props.history} customerAppId={theSubscription.customerAppId}/>
                          </Paper>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                );
              }
              return <LoadingSpinner />
            }}
          />
        )
      } else {
        return (
          <Redirect to='/home' />
        );
      }
    }

    goToExternalApp(action, customerAppId, appId, appLink) {
      abEnvironment.goToExternalApp(action, customerAppId, appId, appLink);
    }
  
    _updateCustomerApp(theApp, theSubscription) {

      const { callPartnerApi, encryptOneTimeEncryptedValue } = this.props.auth;
      this.setState({ saveStatus: 'saving' });

      let theDomain = null;
      if (theApp.showDomain === 1) {
        theDomain = this.state.domain;
      }

      let theUsername = null;
      if (theApp.showUsername === 1) {
        theUsername = this.state.username;
      }

      let forceFetch = this.state.forceFetch;
      forceFetch++;
  
      Promise.resolve()  
      .then(() => {
        if (this.state.unencryptPwdChanged === true) {
          // encrypt
          return encryptOneTimeEncryptedValue(this.state.unencryptPwd, callPartnerApi);
        } else {
          return null;
        }
      })
      .then((jresp) => {
        if (jresp != null && jresp.status === 0) {
          let theKeyId = jresp.keyId;
          let encryptedData = jresp.encryptedData;

          let payload = {
            operation: "updateSubscription",
            params: {
              "usingAccountId": theSubscription.customerAccountId,
              "provisioningAccountId": theSubscription.provisioningAccountId,
              "customerAppId": theSubscription.customerAppId,
              "keyId": theKeyId,
              "encryptedData": encryptedData,
              "appStatus": null,
              "appSkuPrice": null, 
              "appMarkupPercentage": null, 
              "appMarkupAbsolute": null, 
              "notes": null,
              "paymentOrder": null,
              "useEmail": this.state.useEmail,
              "shareCredentials": null, 
              "showSubscription": null, 
              "passThroughFees": null, 
              "wholesaleDiscountPercentage": null, 
              "domain": theDomain, 
              "username": theUsername,
              "forwardingEmailList": null
            }
          };

          return callPartnerApi('appbind/generic',
                                'POST',
                                payload);
        } else {
          let payload = {
            operation: "updateSubscription",
            params: {
              "usingAccountId": theSubscription.customerAccountId,
              "provisioningAccountId": theSubscription.provisioningAccountId,
              "customerAppId": theSubscription.customerAppId,
              "unencryptedString": this.state.unencryptPwd,
              "appStatus": null,
              "appSkuPrice": null, 
              "appMarkupPercentage": null, 
              "appMarkupAbsolute": null, 
              "notes": null,
              "paymentOrder": null,
              "useEmail": this.state.useEmail,
              "shareCredentials": null, 
              "showSubscription": null, 
              "passThroughFees": null, 
              "wholesaleDiscountPercentage": null, 
              "domain": theDomain, 
              "username": theUsername,
              "forwardingEmailList": null
    
            }
          };

          return callPartnerApi('appbind/generic',
                                'POST',
                                payload);
        }
      }).then(()=> {
        this.setState({ forceFetch: forceFetch, unencryptPwdChanged: false, saveSnackbarOpen: true, saveStatus: 'success', saveMessage: 'Subscription saved.', saveSnackbarDuration: 3000 });
      })
      .catch(error => {
        //TODO comment out
        //console.log('Error: ' + error);
      });
    }
  
    _onSnackbarClose() {
      this.setState( {saveSnackbarOpen: false, saveStatus: '', saveMessage: '', saveSnackbarDuration: null });
    }
  }

SubscriptionView.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styleSheet)(SubscriptionView);
  